import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg";

interface GlyphScore {
  label: string;
  glyph: string;
  score?: number | null;
}

interface TossScoreOrbProps {
  score: number;
  size?: Size;
  label?: string;
  glyphs?: GlyphScore[];
  className?: string;
  showLabel?: boolean;
}

const SIZE_MAP: Record<Size, { wrap: string; ring: number; numeral: string; tag: string }> = {
  sm: { wrap: "w-14 h-14", ring: 4, numeral: "text-xl", tag: "text-[8px]" },
  md: { wrap: "w-24 h-24", ring: 6, numeral: "text-3xl", tag: "text-[10px]" },
  lg: { wrap: "w-40 h-40", ring: 8, numeral: "text-6xl", tag: "text-xs" },
};

export function tossBand(score: number) {
  if (score >= 80) return { token: "toss-elite", label: "Elite" };
  if (score >= 65) return { token: "toss-high", label: "Strong" };
  if (score >= 40) return { token: "toss-mid", label: "Moderate" };
  return { token: "toss-low", label: "Weak" };
}

export function glyphColor(s?: number | null) {
  if (s == null || !isFinite(s)) return "text-muted-foreground";
  if (s >= 70) return "text-[hsl(var(--toss-elite))]";
  if (s >= 50) return "text-[hsl(var(--toss-high))]";
  if (s >= 30) return "text-[hsl(var(--toss-mid))]";
  return "text-[hsl(var(--toss-low))]";
}

export function TossScoreOrb({
  score,
  size = "md",
  label = "TOSS",
  glyphs,
  className,
  showLabel = true,
}: TossScoreOrbProps) {
  const safe = Math.max(0, Math.min(100, Math.round(score)));
  const cfg = SIZE_MAP[size];
  const band = tossBand(safe);
  const isElite = safe >= 80;

  // Conic ring background
  const ringStyle: React.CSSProperties = {
    background: `conic-gradient(hsl(var(--${band.token})) ${safe * 3.6}deg, hsl(var(--muted) / 0.18) 0deg)`,
    padding: cfg.ring,
  };

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div
        className={cn(
          "relative rounded-full flex items-center justify-center shrink-0",
          cfg.wrap,
          isElite && "orb-pulse"
        )}
        style={ringStyle}
      >
        <div className="w-full h-full rounded-full bg-card flex flex-col items-center justify-center border border-border/40">
          <span
            className={cn(
              "font-mono font-black tabular-nums leading-none",
              cfg.numeral,
              `text-[hsl(var(--${band.token}))]`
            )}
          >
            {safe}
          </span>
          {showLabel && (
            <Link
              to="/how-toss-works"
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "uppercase tracking-[0.18em] text-muted-foreground mt-0.5 hover:text-primary transition-colors",
                cfg.tag,
              )}
              title="How TOSS Works"
            >
              {label}
            </Link>
          )}
        </div>
      </div>

      {glyphs && glyphs.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap justify-center max-w-[160px]">
          {glyphs.map((g) => (
            <span
              key={g.label}
              title={`${g.label}${g.score != null ? `: ${Math.round(g.score)}` : ""}`}
              className={cn(
                "inline-flex items-center gap-0.5 text-[10px] font-mono font-semibold",
                glyphColor(g.score)
              )}
            >
              <span className="text-sm leading-none">{g.glyph}</span>
              {g.score != null && <span className="tabular-nums">{Math.round(g.score)}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
